'use client';

import { memo, startTransition, useState, useMemo } from 'react';
import { Text } from '@shopify/polaris';
import { ImagesModalSectionProps } from '@/features/media-modal/components/common/commonTypes';
import { MediaAssetType } from '@/types/media';
import { ModalContentTabsLayout } from './sections-content/ModalContentTabsLayout';
import StockImagesModalSection from './sections-content/StockImagesModalContent';
import StockVideosModalSection from './sections-content/StockVideosModalSection';

enum Tab {
  Videos = 'Videos',
  Photos = 'Photos',
}

const StockMediaModalSection = (props: ImagesModalSectionProps) => {
  const preferredType = props.preferredMediaAssetType;
  const fixedTab = preferredType ? (preferredType === MediaAssetType.video ? Tab.Videos : Tab.Photos) : null;

  const tabs = useMemo(() => {
    if (fixedTab) {
      return [
        {
          id: fixedTab,
          content: fixedTab,
        },
      ];
    }
    if (props.isOnlyImages) {
      return [
        {
          id: Tab.Photos,
          content: 'Photos',
        },
      ];
    }
    return [
      {
        id: Tab.Videos,
        content: 'Videos',
      },
      {
        id: Tab.Photos,
        content: 'Photos',
      },
    ];
  }, [fixedTab, props.isOnlyImages]);

  const [activeTab, setActiveTab] = useState<Tab>(() => {
    if (fixedTab) {
      return fixedTab;
    }
    return props.isOnlyImages ? Tab.Photos : Tab.Videos;
  });

  const tab = fixedTab || activeTab;

  const handleTabChange = (tab: Tab) => {
    if (!fixedTab) {
      startTransition(() => {
        setActiveTab(tab);
      });
    }
  };

  const content = tab === Tab.Videos ? <StockVideosModalSection {...props} /> : <StockImagesModalSection {...props} />;

  if (fixedTab) {
    return (
      <div className="flex h-full w-full flex-col gap-1">
        <Text as="h2" variant="headingMd" fontWeight="bold">
          {fixedTab === Tab.Videos ? 'Stock Videos' : 'Stock Photos'}
        </Text>
        <div className="h-full pt-2">{content}</div>
      </div>
    );
  }

  return (
    <ModalContentTabsLayout title="Stock media" tabs={tabs} tab={tab} onTabChange={handleTabChange}>
      {content}
    </ModalContentTabsLayout>
  );
};

export default memo(StockMediaModalSection);
